import { useState, useCallback } from 'react';
import {
  Upload, FileText, CheckCircle2, XCircle, Loader2, Trash2, Copy, ExternalLink,
  AlertTriangle, Archive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssessmentUpload({ token, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [region, setRegion] = useState('auto');
  const [assessmentType, setAssessmentType] = useState('');
  const [instructions, setInstructions] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback((newFiles) => {
    const validFiles = Array.from(newFiles).filter(
      f => f.name.endsWith('.docx') || f.name.endsWith('.zip')
    );
    if (validFiles.length === 0) {
      toast.error('Only .docx and .zip files are supported');
      return;
    }
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return toast.error('Please select at least one file');
    if (!assessmentType) return toast.error('Please select an assessment type');

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('region', region);
    formData.append('assessment_type', assessmentType);
    formData.append('instructions', instructions);

    // Use XMLHttpRequest to avoid 'Request object could not be cloned' error
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/super-support/upload-assessment`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.onload = function () {
      setUploading(false);
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        if (xhr.status === 413) {
          toast.error('File too large. Please try uploading fewer files or a smaller ZIP.');
        } else {
          toast.error('Server error (' + xhr.status + '). Please try again or upload fewer files at once.');
        }
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        toast.error(String(data.detail || 'Upload failed'));
        return;
      }

      setUploadResult(data);

      const msg = [];
      if (data.uploaded > 0) msg.push(data.uploaded + ' uploaded');
      if (data.duplicatesSkipped > 0) msg.push(data.duplicatesSkipped + ' duplicates skipped');
      if (data.failed > 0) msg.push(data.failed + ' failed');

      if (data.uploaded > 0) {
        toast.success(msg.join(', '));
      } else if (data.duplicatesSkipped > 0) {
        toast.warning('All files were duplicates. ' + msg.join(', '));
      } else {
        toast.error('Upload failed. ' + msg.join(', '));
      }

      setFiles([]);
      if (data.uploaded > 0 && onSuccess) onSuccess();
    };

    xhr.onerror = function () {
      setUploading(false);
      toast.error('Network error: Could not connect to server. Please check your connection and try again.');
    };

    xhr.ontimeout = function () {
      setUploading(false);
      toast.error('Upload timed out. Please try uploading fewer files at a time.');
    };

    xhr.timeout = 300000; // 5 minute timeout for large ZIPs
    xhr.send(formData);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getFileIcon = (name) => {
    if (name.endsWith('.zip')) return <Archive className="w-4 h-4 text-amber-600" />;
    return <FileText className="w-4 h-4 text-cyan-600" />;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-900">Upload Assessment</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Upload Form */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Assessment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="upload-region">
                    <SelectValue placeholder="Auto-detect from document" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect from document</SelectItem>
                    <SelectItem value="usa">USA</SelectItem>
                    <SelectItem value="canada">Canada</SelectItem>
                    <SelectItem value="jamaica">Jamaica</SelectItem>
                    <SelectItem value="barbados">Barbados</SelectItem>
                    <SelectItem value="trinidad_tobago">Trinidad & Tobago</SelectItem>
                    <SelectItem value="panama">Panama</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Leave as "Auto-detect" to let the system determine the region from the document content</p>
              </div>

              <div>
                <Label>Assessment Type *</Label>
                <Select value={assessmentType} onValueChange={setAssessmentType}>
                  <SelectTrigger data-testid="upload-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self_assessment">Self Assessment</SelectItem>
                    <SelectItem value="manager_validation">Manager Validation</SelectItem>
                    <SelectItem value="technical">Technical Assessment</SelectItem>
                    <SelectItem value="simulation">Hands-On Simulation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Form Instructions / Prompt</Label>
                <p className="text-xs text-slate-500 mb-1">
                  Paste any instructions that describe how this form should be rendered (e.g., which fields are fillable, which are checkboxes, radio buttons, etc.)
                </p>
                <Textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Paste form instructions here... e.g., 'This form has fillable text fields for Section 1, checkbox options in Section 2.9, radio button scales in Section 3...'"
                  className="min-h-[160px] text-sm"
                  data-testid="upload-instructions"
                />
              </div>
            </CardContent>
          </Card>

          {/* File Upload Area */}
          <Card>
            <CardContent className="p-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                  dragOver ? 'border-cyan-400 bg-cyan-50' : 'border-slate-300 hover:border-slate-400'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input-ss').click()}
                data-testid="upload-dropzone"
              >
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">Drop files here or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">
                  Supports <strong>.docx</strong> and <strong>.zip</strong> files (ZIP files are extracted automatically)
                </p>
                <p className="text-xs text-slate-400 mt-1">Duplicates are automatically detected and skipped</p>
                <input
                  id="file-input-ss"
                  type="file"
                  accept=".docx,.zip"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <Label className="text-xs text-slate-500">{files.length} file(s) selected</Label>
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded p-2">
                      <div className="flex items-center gap-2 text-sm">
                        {getFileIcon(f.name)}
                        <span className="truncate max-w-[250px]">{f.name}</span>
                        <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
                        {f.name.endsWith('.zip') && (
                          <Badge className="bg-amber-50 text-amber-700 text-[10px]">ZIP</Badge>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(i)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700"
                disabled={uploading || files.length === 0 || !assessmentType}
                onClick={handleUpload}
                data-testid="upload-submit-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing & Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: Results */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Upload Results</CardTitle>
            </CardHeader>
            <CardContent>
              {!uploadResult ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  Upload results will appear here
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="flex gap-3 flex-wrap">
                    {uploadResult.uploaded > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> {uploadResult.uploaded} uploaded
                      </Badge>
                    )}
                    {uploadResult.duplicatesSkipped > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {uploadResult.duplicatesSkipped} duplicates skipped
                      </Badge>
                    )}
                    {uploadResult.failed > 0 && (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3 mr-1" /> {uploadResult.failed} failed
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {uploadResult.totalFiles} total files processed
                    </Badge>
                  </div>

                  {/* Successful uploads */}
                  {uploadResult.results?.map((r, i) => (
                    <div key={i} className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="font-medium text-sm text-emerald-800">{r.fullName}</span>
                        <Badge variant="outline" className="text-[10px]">{r.region}</Badge>
                      </div>
                      {r.assessmentLink && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-500">Link:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs flex-1 truncate">{r.assessmentLink}</code>
                          <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => copyToClipboard(window.location.origin + r.assessmentLink)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                          <a href={r.assessmentLink} target="_blank" rel="noreferrer" className="text-cyan-600">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      {r.talentAccount?.temporaryPassword && (
                        <div className="mt-2 text-xs bg-white rounded p-2">
                          <span className="text-slate-500">Temp Password:</span>
                          <code className="ml-2 text-slate-700">{r.talentAccount.temporaryPassword}</code>
                          <Button variant="ghost" size="sm" className="h-5 px-1 ml-1" onClick={() => copyToClipboard(r.talentAccount.temporaryPassword)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Duplicates */}
                  {uploadResult.duplicates?.map((d, i) => (
                    <div key={i} className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-sm text-amber-800">{d.filename}</span>
                      </div>
                      <p className="text-xs text-amber-700">{d.reason}</p>
                      {d.existingLink && (
                        <a href={d.existingLink} target="_blank" rel="noreferrer" className="text-xs text-cyan-600 flex items-center gap-1 mt-1">
                          <ExternalLink className="w-3 h-3" /> View existing assessment
                        </a>
                      )}
                    </div>
                  ))}

                  {/* Errors */}
                  {uploadResult.errors?.map((e, i) => (
                    <div key={i} className="border rounded-lg p-3 bg-red-50 border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="font-medium text-sm text-red-800">{e.filename}</span>
                      </div>
                      <p className="text-xs text-red-700">{e.error}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
